import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { BooleanFilter, DateFilter, IQueryCommand, NumberFilter, StringFilter, StringFilterMatchMode } from "@features/filter";
import { ControlBase } from "@mipro/custom-controls";
import { SelectItem } from "primeng/api";
import { IEpisode } from "@project/models/episodes/episode";
import { EpisodeService } from "../episode.service";
import { EpisodeSelectMode } from "./episode-select-mode";
import { EpisodeSelectOption } from "./episode-select-option";
import {
    createGenericPrescriptionEpisode,
    createRoutineTreatmentEpisode,
    isGenericPrescriptionEpisode,
    isRoutineTreatmentEpisode,
    createSelfDirectedGymRecoveryEpisode,
    isSelfDirectedGymRecoveryEpisode
} from "./episode-defaults";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { Subscription } from "rxjs";
import { WalkIn } from "@project/walk-ins/classes/walk-in";
import { EpisodeStatus } from "@project/models/episodes/episode-status";
import { RavenApiService } from "@main/raven/raven-api.service";
import { IRavenIssue } from "@main/raven/models/raven-issue";
import { Router } from "@angular/router";
import { AppStorage } from "@bbwt/utils/app-storage";
import { EpisodeCreatedFrom } from "@project/models/episodes/episode_createdFrom";
import { IPagedData } from "@features/grid";
import { GenericDiagnosisCodes } from "@project/diagnosis/generic-codes";
import { UserHelperService } from "@main/users/user-helper.service";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";

@Component({
    selector: "episode-select",
    templateUrl: "./episode-select-control.component.html",
    styleUrls: ["./episode-select-control.component.scss"],
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: EpisodeSelectControlComponent, multi: true }]
})
export class EpisodeSelectControlComponent extends ControlBase<EpisodeSelectOption> implements OnInit, OnDestroy, AfterViewInit {
    private _patientId: string;
    private _disabled: boolean = false;

    draftedEpisode: IEpisode;
    EpisodeCreatedFrom = EpisodeCreatedFrom;
    @Input() isWalkInDraft = false;

    @Input() tempWalkIn: WalkIn | any = null;
    @Input() restoreDuplicateCheakState: boolean = false;
    @Input() restoreSelectControlState: boolean = false;
    @Input() restoreSelectDialog: boolean = true;
    @Input() isPrescription: boolean = false;
    @Input() assignedClinicianId: string;
    @Input() assignedClinicianVisible: boolean = true;
    @Input() incidentRavenIssue: IRavenIssue = null;
    @Input() incidentNotes: string = null;
    @Input() selectedWalkInDate: Date = null;
    @Input() fromEdit: boolean = false;

    @Input() episodeId: string;
    @Input() episode: IEpisode;

    @Input() createFrom: EpisodeCreatedFrom;

    @Input() showClear: boolean = true;

    @Input()
    selectionModes: EpisodeSelectMode[] = [
        EpisodeSelectMode.RoutineTreatment,
        EpisodeSelectMode.ExistingEpisode,
        EpisodeSelectMode.NewEpisode
    ];

    @Input() offlineInvalidModes: EpisodeSelectMode[] = [EpisodeSelectMode.ExistingEpisode];
    private originalSelectedMode: EpisodeSelectMode;
    @Input() originallySelectedOption: EpisodeSelectOption;

    episodesLoading: boolean;
    @Input() surveyInstance: boolean = false;

    @Input() dataInsertion = false;

    @Input()
    set patientId(id: string) {
        this._patientId = id != null ? id : this.patientService.currentPatientId;
        this.refreshExistingEpisodes();
    }

    loadMoreStep = 1;
    loadMoreDisabled = false;
    pageSize = 3;

    currentDate: Date = new Date();
    get patientId() {
        return this._patientId;
    }

    @Input() needsRemovedSelfDirectedGymRecoveryMode = false;
    // Form Instance Data Insertion

    @Output() readonly episodeModeSelected = new EventEmitter<EpisodeSelectMode>();
    @Output() readonly onClear = new EventEmitter();

    @Output() readonly emitEpisodeNavigation = new EventEmitter();
    @Output() readonly viewEpisode = new EventEmitter<string>();

    @Input() set disabled(disabled: boolean) {
        this._disabled = disabled;
        this.setDisabledState(disabled);
    }

    get disabled() {
        return this._disabled;
    }

    selectedOption: EpisodeSelectOption;

    private originalDdOptions = <SelectItem<EpisodeSelectOption>[]>[
        {
            value: { episode: createRoutineTreatmentEpisode(this.patientId), selectMode: EpisodeSelectMode.RoutineTreatment },
            icon: "pi pi-replay",
            label: "Routine Treatment"
        },
        {
            value: { episode: createSelfDirectedGymRecoveryEpisode(this.patientId), selectMode: EpisodeSelectMode.SelfDirectedGymRecovery },
            icon: "pi pi-replay",
            label: GenericDiagnosisCodes.GymRecoveryDiagnosisName
        },
        {
            value: {
                episode: createGenericPrescriptionEpisode(this.patientId),
                selectMode: EpisodeSelectMode.GenericPrescriptionEpisode
            },
            icon: "pi pi-replay",
            label: "Generic Prescription Episode"
        },
        {
            value: { episode: null, selectMode: EpisodeSelectMode.ExistingEpisode },
            icon: "pi pi-list",
            label: "Existing Episode"
        },
        {
            value: { episode: null, selectMode: EpisodeSelectMode.NewEpisode },
            icon: "pi pi-plus",
            label: "Add Episode",
            disabled: this.userHelperService.isPatient
        },
        {
            value: { episode: null, selectMode: EpisodeSelectMode.EditEpisode },
            icon: "pi pi-pencil",
            label: "Edit Episode",
            disabled: this.userHelperService.isPatient
        },
        {
            value: { episode: null, selectMode: EpisodeSelectMode.None },
            disabled: true
        }
    ];

    onlineOptions = <SelectItem<EpisodeSelectOption>[]>[
        {
            value: { episode: null, selectMode: EpisodeSelectMode.ExistingEpisode },
            icon: "pi pi-list",
            label: "Existing Episode"
        }
    ];

    ddOptions: SelectItem<EpisodeSelectOption>[];

    displaySelectExistingEpisodeDialog = false;
    existingEpisodes: IEpisode[] = [];
    selectedExistingEpisodeId: string;
    displayAddEpisodeDialog = false;
    selectedExistingEpisode = false;
    private onlineChangeSuscription: Subscription;
    displayEditEpisodeDialog = false;

    private ravenEnabled: boolean = false;

    constructor(
        private broadcastService: BroadcastService,
        private episodeService: EpisodeService,
        private userHelperService: UserHelperService,
        private appOnlineStateService: AppOnlineStateService,
        private ravenApiService: RavenApiService,
        private router: Router,
        private patientService: PatientService
    ) {
        super();
    }

    onClick() {
        if (this.needsRemovedSelfDirectedGymRecoveryMode) {
            this.disableSelectionMode(EpisodeSelectMode.SelfDirectedGymRecovery);
            this.needsRemovedSelfDirectedGymRecoveryMode = false;
        }
    }

    async ngAfterViewInit() {
        if (this.restoreDuplicateCheakState) {
            this.displayAddEpisodeDialog = true;
            this.selectedOption = <EpisodeSelectOption>{
                selectMode: EpisodeSelectMode.NewEpisode,
                episode: null
            };
        }

        if (this.restoreSelectControlState) {
            if (this.restoreSelectDialog) {
                this.displaySelectExistingEpisodeDialog = true;
                this.selectedOption = <EpisodeSelectOption>{
                    selectMode: EpisodeSelectMode.ExistingEpisode,
                    episode: null
                };
            } else {
                this.selectedOption = <EpisodeSelectOption>{
                    selectMode: EpisodeSelectMode.ExistingEpisode,
                    episode: this.episode
                };
            }
        }
    }

    ngOnInit(): void {
        this.ravenEnabled = this.ravenApiService.isRavenModuleEnabled();
        this.initOptions();

        this.onlineChangeSuscription = this.broadcastService
            .on<{ isAppOnline: boolean }>(AppOnlineStateService.AppOnlineStateChanged)
            .subscribe(data => this.initOptions(data.isAppOnline));
    }

    ngOnDestroy(): void {
        if (this.onlineChangeSuscription) {
            this.onlineChangeSuscription.unsubscribe();
        }
    }

    initOptions(appBackOnline?: boolean) {
        this.selectionModes ??= [];
        if (appBackOnline === undefined) {
            // First load
            this.ddOptions = this.originalDdOptions.filter(opt => this.selectionModes.includes(opt.value.selectMode));
        }

        if (this.appOnlineStateService.isAppOnline === false) {
            // App goes offline (or starts offline)
            this.offlineInvalidModes.forEach(sm => this.disableSelectionMode(sm)); // Disable every option which is not valid in offline mode
            if (this.selectedOption?.selectMode == EpisodeSelectMode.ExistingEpisode) {
                this.setEpisodeAndNotifyOnChange(this.selectedOption.episode, EpisodeSelectMode.None); // Change Existing for a placeholder
            }
        } else {
            // App goes back online (or starts online)
            this.selectionModes.forEach(sm => {
                this.disableSelectionMode(sm); // Disable every option first and re-enable them to shown them in the same order
                if (sm != EpisodeSelectMode.None || (sm == EpisodeSelectMode.None && this.ravenEnabled) || this.selectedOption) {
                    // Placeholder shouldn't be shown when there's nothing selected
                    if (!this.surveyInstance) {
                        this.enableSelectionMode(sm);
                    } else if (sm == EpisodeSelectMode.ExistingEpisode) {
                        this.enableSelectionMode(sm);
                    }
                }
            });
            if (this.selectedOption?.selectMode == EpisodeSelectMode.None) {
                // Reconcile the selected episode with the available selection modes.
                this.disableSelectionMode(EpisodeSelectMode.EditEpisode);
                this.disableSelectionMode(EpisodeSelectMode.None);
                this.setEpisodeAndNotifyOnChange(
                    this.selectedOption.episode,
                    this.ravenEnabled ? EpisodeSelectMode.None : EpisodeSelectMode.ExistingEpisode
                );
            }
        }
    }

    writeValue(obj: EpisodeSelectOption): void {
        if (!obj) {
            this.selectedOption = null;
            return;
        }

        const option = this.ddOptions.find(opt => opt.value.selectMode === obj.selectMode);
        if (option) {
            option.value.episode = obj.episode;
            this.selectedOption = option.value;
            this.originalSelectedMode = obj.selectMode;
            if (!this.originallySelectedOption) {
                this.originallySelectedOption = this.selectedOption;
            }
        }

        this.updateOptions();
    }

    selectExistingEpisode() {
        this.setEpisodeAndNotifyOnChange(
            this.existingEpisodes.find(e => e.id === this.selectedExistingEpisodeId),
            this.ravenEnabled || !this.appOnlineStateService.isAppOnline ? EpisodeSelectMode.None : EpisodeSelectMode.ExistingEpisode
        );
        this.selectedExistingEpisodeId = null;
        this.selectedExistingEpisode = true;
        this.displaySelectExistingEpisodeDialog = false;

        document.body.style.overflow = "auto";
    }

    cancelExistingEpisode() {
        this.selectedExistingEpisode = false;
        this.displaySelectExistingEpisodeDialog = false;
    }

    closeExistingEpisode() {
        if (!this.selectedExistingEpisode) {
            this.selectedExistingEpisodeId = null;
            if (!this.originallySelectedOption?.episode) {
                this.setEpisodeAndNotifyOnChange(null);
            } else {
                this.setEpisodeAndNotifyOnChange(this.originallySelectedOption.episode);
            }
        }
    }

    cancelAddEpisode() {
        this.restoreDuplicateCheakState = false;
        this.selectedExistingEpisodeId = null;
        if (!this.originallySelectedOption?.episode) {
            this.setEpisodeAndNotifyOnChange(null);
        } else {
            this.setEpisodeAndNotifyOnChange(this.originallySelectedOption.episode);
        }
        this.displayAddEpisodeDialog = false;
    }

    cancelEditEpisode(episode: IEpisode) {
        if (episode) {
            this.setEpisodeAndNotifyOnChange(
                episode,
                this.ravenEnabled || !this.appOnlineStateService.isAppOnline ? EpisodeSelectMode.None : EpisodeSelectMode.ExistingEpisode
            );
        }
        this.displayEditEpisodeDialog = false;
    }

    closeAddEpisodeDialog(episode: IEpisode): void {
        if (episode) {
            this.episodeId = episode.id;
            this.draftedEpisode = episode;
            this.ddOptions.forEach(x => {
                x.disabled = false;
            });
        }
        this.setEpisodeAndNotifyOnChange(
            episode,
            this.ravenEnabled || !this.appOnlineStateService.isAppOnline ? EpisodeSelectMode.None : EpisodeSelectMode.ExistingEpisode
        );
        this.displayAddEpisodeDialog = false;
    }

    closeEditEpisodeDialog(episode: IEpisode): void {
        this.setEpisodeAndNotifyOnChange(
            episode,
            this.ravenEnabled || !this.appOnlineStateService.isAppOnline ? EpisodeSelectMode.None : EpisodeSelectMode.ExistingEpisode
        );
        this.displayEditEpisodeDialog = false;
    }

    onSelectEpisodeModeChange(option: EpisodeSelectOption) {
        if (!option) {
            this.setEpisodeAndNotifyOnChange(null);
            return;
        }

        this.episodeModeSelected?.emit(option.selectMode);

        switch (option.selectMode) {
            case EpisodeSelectMode.ExistingEpisode:
                this.displaySelectExistingEpisodeDialog = true;
                break;

            case EpisodeSelectMode.NewEpisode:
                this.displayAddEpisodeDialog = true;
                break;

            case EpisodeSelectMode.EditEpisode:
                this.episodeId = this.selectedOption.episode.id;
                this.draftedEpisode = this.selectedOption.episode;
                this.displayEditEpisodeDialog = true;
                break;

            default:
                this.originallySelectedOption = option;
                this.setEpisodeAndNotifyOnChange(option.episode, option.selectMode);
                break;
        }
    }

    private disableSelectionMode(selectionMode: EpisodeSelectMode) {
        const optionIndex = this.ddOptions.findIndex(o => o.value.selectMode == selectionMode);
        if (optionIndex > -1) {
            this.ddOptions.splice(optionIndex, 1);
        }
    }

    private enableSelectionMode(selectionMode: EpisodeSelectMode, episode?: IEpisode) {
        const option = this.originalDdOptions.find(o => o.value.selectMode == selectionMode);
        if (episode) {
            option.value.episode = episode;
        }
        const optionIndex = this.ddOptions.findIndex(o => o.value.selectMode == selectionMode);
        if (optionIndex > -1) {
            this.ddOptions.splice(optionIndex, 1, option);
        } else {
            if (!(this.fromEdit && option.label === "Add Episode")) {
                this.ddOptions.push(option);
            }
        }
    }

    private setEpisodeAndNotifyOnChange(episode: IEpisode, forceMode?: EpisodeSelectMode) {
        const targetMode = forceMode != null ? forceMode : this.originalSelectedMode || this.selectedOption?.selectMode;

        if (!episode) {
            this.selectedOption = null;
        } else if (!this.selectedOption) {
            // If we're setting an episode and there's not selected option, then make it default to
            // ExistingEpisode, so we don't lose the Episode being set
            this.selectedOption = {
                selectMode: EpisodeSelectMode.ExistingEpisode,
                episode: episode
            };
        }

        if (this.selectedOption) {
            const dropdownOption = this.ddOptions?.find(opt => opt.value.selectMode === targetMode);
            if (dropdownOption) {
                dropdownOption.value.episode = episode;
                this.selectedOption = dropdownOption.value;
            } else {
                this.selectedOption = <EpisodeSelectOption>{
                    selectMode: targetMode,
                    episode: episode
                };
            }
        }

        this.onChange(this.selectedOption);
        this.updateOptions();
    }

    async loadMore() {
        this.loadMoreStep += 1;
        await this.loadExistingEpisodes();
    }

    private async refreshExistingEpisodes(): Promise<any> {
        this.existingEpisodes = [];
        this.loadMoreStep = 1;

        if (!this.patientId) {
            return;
        }
        await this.loadExistingEpisodes();
    }

    async loadExistingEpisodes() {
        const queryCommand = <IQueryCommand>{
            filters: [],
            sortingField: "UpdateDateTime",
            sortingDirection: -1,
            take: this.pageSize * this.loadMoreStep,
            skip: 0
        };

        queryCommand.filters.push(new StringFilter("patiendUserId", this.patientId, StringFilterMatchMode.Equals));

        queryCommand.filters.push(new BooleanFilter("showNotes", false));
        queryCommand.filters.push(new BooleanFilter("showDocuments", false));
        queryCommand.filters.push(new BooleanFilter("showArchived", false));
        queryCommand.filters.push(new BooleanFilter("useFilter", true));
        queryCommand.filters.push(new BooleanFilter("showAll", false));
        queryCommand.filters.push(new BooleanFilter("showAllWithoutLoad", true));
        queryCommand.filters.push(new BooleanFilter("showActive", true));

        if (this.isPrescription) {
            queryCommand.filters.push(new BooleanFilter("showDraftWalkIn", true));
        }

        queryCommand.filters.push(new BooleanFilter("showFormInstances", false));
        queryCommand.filters.push(new NumberFilter("loadMoreMonth", 1));
        queryCommand.filters.push(new DateFilter("gridCreateDate", this.currentDate));

        if (this.dataInsertion) {
            queryCommand.filters.push(new BooleanFilter("private", false));
        }

        if (this.appOnlineStateService.isAppOnline) {
            let page: IPagedData<IEpisode>;
            try {
                this.episodesLoading = true;
                page = await this.episodeService.getPage(queryCommand);
            } finally {
                this.episodesLoading = false;
            }
            this.loadMoreDisabled = page.items.length < this.pageSize * this.loadMoreStep;
            this.existingEpisodes = [...page.items].filter(x => !this.dataInsertion || !x.private);
        }
    }

    private updateOptions() {
        /* When a brand new Episode is selected (and it's not a generic Episode)
         * we should replace the ADD option by the EDIT option.
         */
        if (this.selectedOption) {
            if (
                (this.selectedOption.episode.state == EpisodeStatus.DraftPrescription ||
                    this.selectedOption.episode.state == EpisodeStatus.FormDraft ||
                    this.selectedOption.episode.state == EpisodeStatus.WalkInDraft) &&
                !isGenericPrescriptionEpisode(this.selectedOption.episode) &&
                !isRoutineTreatmentEpisode(this.selectedOption.episode) &&
                !isSelfDirectedGymRecoveryEpisode(this.selectedOption.episode)
            ) {
                this.disableSelectionMode(EpisodeSelectMode.NewEpisode);
                if (!this.surveyInstance && !this.userHelperService.isPatient) {
                    this.enableSelectionMode(EpisodeSelectMode.EditEpisode, this.selectedOption.episode);
                }
            } else {
                this.disableSelectionMode(EpisodeSelectMode.EditEpisode);
                if (!(this.ravenEnabled && this.isPrescription) && !this.userHelperService.isPatient) {
                    this.enableSelectionMode(EpisodeSelectMode.NewEpisode);
                }
            }
        } else {
            // If nothing is selected then there's nothing to be edited
            this.disableSelectionMode(EpisodeSelectMode.EditEpisode);
            if (!(this.ravenEnabled && this.isPrescription) && !this.userHelperService.isPatient) {
                this.enableSelectionMode(EpisodeSelectMode.NewEpisode);
            }
        }

        // When the Raven is enabled or the App is offline then we should have a placeholder for brand new selected episodes
        if (this.ravenEnabled || !this.appOnlineStateService.isAppOnline) {
            if (this.selectedOption) {
                this.enableSelectionMode(EpisodeSelectMode.None, this.selectedOption?.episode);
            } else {
                this.disableSelectionMode(EpisodeSelectMode.None);
            }
        }
    }

    emitViewEpisode(episodeId: string) {
        var url = this.router.url.split("?");
        var onlyUrl = url[0];
        AppStorage.setItemForSession("tempWalkIn", this.tempWalkIn);
        AppStorage.setItemForSession("backUrl", onlyUrl);
        AppStorage.setItemForSession("selectedWalkInDate", this.selectedWalkInDate);
        this.viewEpisode?.emit(episodeId);

        this.router.navigate([`/app/consultation-history/episode/view/${episodeId}`], {
            queryParams: { restoreSelectControlState: true, showPlusButton: false }
        });
    }

    public resetSelection() {
        this.selectedOption = null;
        this.episode = null;
        this.onChange(this.selectedOption);
        this.updateOptions();

        this.enableSelectionMode(EpisodeSelectMode.NewEpisode);
    }
}
